import { CommonModule } from '@angular/common';
import { Component, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastService } from 'angular-toastify';
import {
  ParkingChartDot,
  ParkingDashboardStatCard,
  ParkingGateEntryBar,
  ParkingOpsDailyPoint,
  ParkingOpsDashboardResult,
  ParkingTrafficBar,
} from '../../../../core/models/report/parking-dashboard.model';
import { MapperParkingDashboardService } from '../../../../core/mappers/report/mapper-parking-dashboard.service';
import { ApiParkingDashboardService } from '../../../../core/services/api/report/api-parking-dashboard.service';
import { SiteStateService } from '../../../../core/services/site-state.service';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';

type PeriodMode = 'month' | 'range';
type ChartType = 'line' | 'bar';

@Component({
  selector: 'app-dashboard-parking-ops',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent],
  templateUrl: './dashboard-parking-ops.component.html',
  styleUrl: './dashboard-parking-ops.component.css',
})
export class DashboardParkingOpsComponent {
  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiParkingDashboardService);
  private readonly mapper = inject(MapperParkingDashboardService);
  private readonly toast = inject(ToastService);

  isLoading = signal(false);
  hasSearched = signal(false);
  lastFetchedTime: Date | null = null;
  result = signal<ParkingOpsDashboardResult | null>(null);
  cards = signal<ParkingDashboardStatCard[]>([]);

  periodMode: PeriodMode = 'month';
  monthValue = '';
  startDate = '';
  endDate = '';
  trafficChartType: ChartType = 'bar';
  evChartType: ChartType = 'bar';

  trafficYLabels: number[] = [0, 0, 0, 0, 0];
  evYLabels: number[] = [0, 0, 0, 0, 0];
  entryLinePoints = '';
  exitLinePoints = '';
  entryAreaPoints = '';
  exitAreaPoints = '';
  evEntryLinePoints = '';
  evExitLinePoints = '';
  evEntryAreaPoints = '';
  evExitAreaPoints = '';
  entryDots: ParkingChartDot[] = [];
  exitDots: ParkingChartDot[] = [];
  evEntryDots: ParkingChartDot[] = [];
  evExitDots: ParkingChartDot[] = [];
  trafficBars: ParkingTrafficBar[] = [];
  evBars: ParkingTrafficBar[] = [];
  gateEntryBars: ParkingGateEntryBar[] = [];
  dayLabels: string[] = [];

  hoveredTraffic = signal<number | null>(null);
  hoveredEv = signal<number | null>(null);
  hoveredGate = signal<number | null>(null);

  private readonly maxRangeMonths = 2;

  constructor() {
    const now = new Date();
    this.monthValue = this.toInputMonth(now);
    const { start, end } = this.monthToRange(this.monthValue);
    this.startDate = start;
    this.endDate = end;

    effect(() => {
      const siteId = this.siteState.selectedSite()?.id ?? 0;
      untracked(() => {
        this.clearResult();
        if (siteId > 0) {
          this.loadDashboard();
        }
      });
    });
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get canSearch(): boolean {
    return this.selectedSiteId > 0 && !!this.startDate && !!this.endDate;
  }

  get periodLabel(): string {
    if (!this.startDate || !this.endDate) return '—';
    return `${this.formatDate(this.startDate)} – ${this.formatDate(this.endDate)}`;
  }

  get maxEndDate(): string | undefined {
    if (!this.startDate) return undefined;
    const start = this.parseInputDate(this.startDate);
    if (!start) return undefined;
    return this.toInputDate(
      new Date(start.getFullYear(), start.getMonth() + this.maxRangeMonths, start.getDate()),
    );
  }

  get hasChartData(): boolean {
    return (this.result()?.daily?.length ?? 0) > 0;
  }

  get showEvChart(): boolean {
    return !!this.result()?.flags?.ev;
  }

  get trafficEntryTotal(): number {
    const k = this.result()?.kpis;
    return (k?.entryCount ?? 0) + (k?.evEntryCount ?? 0);
  }

  get trafficExitTotal(): number {
    const k = this.result()?.kpis;
    return (k?.exitCount ?? 0) + (k?.evExitCount ?? 0);
  }

  get hasGateEntryChart(): boolean {
    return (this.result()?.entryByGate?.length ?? 0) > 0;
  }

  get peakGateLabel(): string {
    const peak = this.gateEntryBars.find((g) => g.isPeak);
    if (!peak) return '—';
    return `${peak.gateName} (${peak.entryCount.toLocaleString('th-TH')} คัน)`;
  }

  onPeriodModeChange(mode: PeriodMode): void {
    this.periodMode = mode;
    if (mode === 'month' && this.monthValue) {
      const { start, end } = this.monthToRange(this.monthValue);
      this.startDate = start;
      this.endDate = end;
    }
    this.clearResult();
  }

  onMonthChange(): void {
    if (!this.monthValue) return;
    const { start, end } = this.monthToRange(this.monthValue);
    this.startDate = start;
    this.endDate = end;
    this.clearResult();
  }

  onRangeChange(): void {
    if (this.startDate && this.endDate && this.startDate > this.endDate) {
      this.endDate = this.startDate;
    }
    if (this.startDate && this.endDate && this.isRangeOverMax(this.startDate, this.endDate)) {
      const maxEnd = this.maxEndDate;
      if (maxEnd) this.endDate = maxEnd;
      this.toast.warn('เรียกดูได้สูงสุด 2 เดือน — ปรับวันสิ้นสุดให้อัตโนมัติ');
    }
    this.clearResult();
  }

  onSearch(): void {
    if (!this.selectedSiteId) {
      this.toast.error('กรุณาเลือกลานจาก Navbar');
      return;
    }
    if (!this.startDate || !this.endDate) {
      this.toast.error('กรุณาเลือกช่วงวันที่');
      return;
    }
    if (this.startDate > this.endDate) {
      this.toast.error('วันสิ้นสุดต้องไม่ก่อนวันเริ่มต้น');
      return;
    }
    if (this.isRangeOverMax(this.startDate, this.endDate)) {
      this.toast.error('เรียกดูได้สูงสุด 2 เดือนเท่านั้น');
      return;
    }
    this.loadDashboard();
  }

  onRefresh(): void {
    if (!this.canSearch) {
      this.onSearch();
      return;
    }
    this.loadDashboard();
  }

  loadDashboard(): void {
    if (!this.canSearch) {
      this.clearResult();
      return;
    }

    this.isLoading.set(true);
    this.api.getParkingOpsDashboard({
      siteId: this.selectedSiteId,
      startDate: this.startDate,
      endDate: this.endDate,
    }).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapOps(res);
        this.result.set(mapped);
        this.cards.set(this.mapper.toOpsStatCards(mapped));
        this.renderCharts(mapped.daily);
        this.renderGateEntryBars(mapped.entryByGate);
        this.hasSearched.set(true);
        this.lastFetchedTime = new Date();
        this.isLoading.set(false);
      },
      error: (err) => {
        this.result.set(null);
        this.cards.set([]);
        this.clearCharts();
        this.hasSearched.set(true);
        this.isLoading.set(false);
        this.toast.error(err?.error?.message || 'โหลด Dashboard ไม่สำเร็จ');
      },
    });
  }

  formatDate(value: string | null | undefined): string {
    if (!value) return '—';
    const d = this.parseInputDate(value) ?? new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${d.getFullYear()}`;
  }

  formatShortDate(value: string | null | undefined): string {
    if (!value) return '—';
    const d = this.parseInputDate(value) ?? new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
  }

  private renderCharts(daily: ParkingOpsDailyPoint[]): void {
    this.dayLabels = daily.map((d) => this.formatShortDate(d.date));

    const entryValues = daily.map((d) => d.entryCount);
    const exitValues = daily.map((d) => d.exitCount);
    const evEntryValues = daily.map((d) => d.evEntryCount);
    const evExitValues = daily.map((d) => d.evExitCount);

    const trafficMax = Math.max(...entryValues, ...exitValues, 5);
    const trafficCeil = Math.ceil(trafficMax / 4) * 4 || 4;
    this.trafficYLabels = [0, trafficCeil * 0.25, trafficCeil * 0.5, trafficCeil * 0.75, trafficCeil];

    const evMax = Math.max(...evEntryValues, ...evExitValues, 5);
    const evCeil = Math.ceil(evMax / 4) * 4 || 4;
    this.evYLabels = [0, evCeil * 0.25, evCeil * 0.5, evCeil * 0.75, evCeil];

    const peakEntryIdx = this.indexOfMax(entryValues);
    const peakExitIdx = this.indexOfMax(exitValues);
    const peakEvEntryIdx = this.indexOfMax(evEntryValues);
    const peakEvExitIdx = this.indexOfMax(evExitValues);

    this.trafficBars = daily.map((d, i) => ({
      label: this.dayLabels[i] ?? '',
      fullDate: this.formatDate(d.date),
      entry: d.entryCount,
      exit: d.exitCount,
      entryPct: d.entryCount <= 0 ? 0 : Math.max(3, (d.entryCount / trafficCeil) * 100),
      exitPct: d.exitCount <= 0 ? 0 : Math.max(3, (d.exitCount / trafficCeil) * 100),
      isPeakEntry: i === peakEntryIdx && d.entryCount > 0,
      isPeakExit: i === peakExitIdx && d.exitCount > 0,
    }));

    this.evBars = daily.map((d, i) => ({
      label: this.dayLabels[i] ?? '',
      fullDate: this.formatDate(d.date),
      entry: d.evEntryCount,
      exit: d.evExitCount,
      entryPct: d.evEntryCount <= 0 ? 0 : Math.max(3, (d.evEntryCount / evCeil) * 100),
      exitPct: d.evExitCount <= 0 ? 0 : Math.max(3, (d.evExitCount / evCeil) * 100),
      isPeakEntry: i === peakEvEntryIdx && d.evEntryCount > 0,
      isPeakExit: i === peakEvExitIdx && d.evExitCount > 0,
    }));

    const entryCoords = this.toLineCoords(entryValues, trafficCeil);
    const exitCoords = this.toLineCoords(exitValues, trafficCeil);
    const evEntryCoords = this.toLineCoords(evEntryValues, evCeil);
    const evExitCoords = this.toLineCoords(evExitValues, evCeil);

    this.entryLinePoints = entryCoords.map((p) => `${p.x},${p.y}`).join(' ');
    this.exitLinePoints = exitCoords.map((p) => `${p.x},${p.y}`).join(' ');
    this.evEntryLinePoints = evEntryCoords.map((p) => `${p.x},${p.y}`).join(' ');
    this.evExitLinePoints = evExitCoords.map((p) => `${p.x},${p.y}`).join(' ');

    this.entryAreaPoints = this.toArea(entryCoords);
    this.exitAreaPoints = this.toArea(exitCoords);
    this.evEntryAreaPoints = this.toArea(evEntryCoords);
    this.evExitAreaPoints = this.toArea(evExitCoords);

    this.entryDots = entryCoords.map((p, i) => ({
      x: p.x,
      y: p.y,
      label: this.dayLabels[i] ?? '',
      value: entryValues[i] ?? 0,
      color: '#0ea5e9',
      isPeak: i === peakEntryIdx && (entryValues[i] ?? 0) > 0,
    }));
    this.exitDots = exitCoords.map((p, i) => ({
      x: p.x,
      y: p.y,
      label: this.dayLabels[i] ?? '',
      value: exitValues[i] ?? 0,
      color: '#6366f1',
      isPeak: i === peakExitIdx && (exitValues[i] ?? 0) > 0,
    }));
    this.evEntryDots = evEntryCoords.map((p, i) => ({
      x: p.x,
      y: p.y,
      label: this.dayLabels[i] ?? '',
      value: evEntryValues[i] ?? 0,
      color: '#14b8a6',
      isPeak: i === peakEvEntryIdx && (evEntryValues[i] ?? 0) > 0,
    }));
    this.evExitDots = evExitCoords.map((p, i) => ({
      x: p.x,
      y: p.y,
      label: this.dayLabels[i] ?? '',
      value: evExitValues[i] ?? 0,
      color: '#0d9488',
      isPeak: i === peakEvExitIdx && (evExitValues[i] ?? 0) > 0,
    }));
  }

  private renderGateEntryBars(
    gates: { gateId: number; gateName: string; entryCount: number }[],
  ): void {
    const list = gates ?? [];
    const max = Math.max(...list.map((g) => g.entryCount), 1);
    const total = list.reduce((s, g) => s + g.entryCount, 0) || 1;
    const peakCount = Math.max(...list.map((g) => g.entryCount), 0);

    this.gateEntryBars = list.map((g) => ({
      gateId: g.gateId,
      gateName: g.gateName || `ประตู #${g.gateId}`,
      entryCount: g.entryCount,
      pct: Math.max(4, (g.entryCount / max) * 100),
      sharePct: Math.round((g.entryCount / total) * 1000) / 10,
      isPeak: g.entryCount > 0 && g.entryCount === peakCount,
    }));
  }

  private indexOfMax(values: number[]): number {
    let max = -1;
    let idx = 0;
    values.forEach((v, i) => {
      if (v > max) {
        max = v;
        idx = i;
      }
    });
    return idx;
  }

  private toLineCoords(values: number[], maxVal: number): { x: number; y: number }[] {
    const padL = 28;
    const padR = 16;
    const padT = 20;
    const padB = 28;
    const w = 640 - padL - padR;
    const h = 240 - padT - padB;
    const total = values.length;
    return values.map((val, index) => {
      const x = total <= 1 ? padL + w / 2 : padL + (index / (total - 1)) * w;
      const y = padT + h - (val / maxVal) * h;
      return { x, y };
    });
  }

  private toArea(points: { x: number; y: number }[]): string {
    if (points.length === 0) return '';
    const baseline = 212;
    const first = points[0];
    const last = points[points.length - 1];
    return `${first.x},${baseline} ${points.map((p) => `${p.x},${p.y}`).join(' ')} ${last.x},${baseline}`;
  }

  private clearCharts(): void {
    this.trafficYLabels = [0, 0, 0, 0, 0];
    this.evYLabels = [0, 0, 0, 0, 0];
    this.entryLinePoints = '';
    this.exitLinePoints = '';
    this.entryAreaPoints = '';
    this.exitAreaPoints = '';
    this.evEntryLinePoints = '';
    this.evExitLinePoints = '';
    this.evEntryAreaPoints = '';
    this.evExitAreaPoints = '';
    this.entryDots = [];
    this.exitDots = [];
    this.evEntryDots = [];
    this.evExitDots = [];
    this.trafficBars = [];
    this.evBars = [];
    this.gateEntryBars = [];
    this.dayLabels = [];
    this.hoveredTraffic.set(null);
    this.hoveredEv.set(null);
    this.hoveredGate.set(null);
  }

  private clearResult(): void {
    this.result.set(null);
    this.cards.set([]);
    this.clearCharts();
    this.hasSearched.set(false);
    this.lastFetchedTime = null;
  }

  private monthToRange(monthValue: string): { start: string; end: string } {
    const m = /^(\d{4})-(\d{2})$/.exec(monthValue.trim());
    if (!m) {
      const today = this.toInputDate(new Date());
      return { start: today, end: today };
    }
    const year = Number(m[1]);
    const monthIndex = Number(m[2]) - 1;
    const start = new Date(year, monthIndex, 1);
    const end = new Date(year, monthIndex + 1, 0);
    return { start: this.toInputDate(start), end: this.toInputDate(end) };
  }

  private toInputMonth(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
  }

  private toInputDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private parseInputDate(value: string): Date | null {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
    if (!m) return null;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }

  private isRangeOverMax(startStr: string, endStr: string): boolean {
    const start = this.parseInputDate(startStr);
    const end = this.parseInputDate(endStr);
    if (!start || !end) return false;
    const maxEnd = new Date(start.getFullYear(), start.getMonth() + this.maxRangeMonths, start.getDate());
    return end > maxEnd;
  }
}
